import { omit } from 'lodash-es';
import posthog from 'posthog-js';
import { useCallback, useMemo, useRef, useState } from 'react';

import { areAnalyticsEnabledInEnv } from '../../areAnalyticsEnabledInEnv';
import useQueueUntilReady from '../../utils/useQueueUntilReady';
import type { Integration, PageViewProps, UserData } from '../types';
import { redactRequest } from './redactData';

const postHogUrl = 'https://eu.i.posthog.com';

const usePostHogIntegration = (): Integration => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationState = useRef<'idle' | 'loading' | 'done'>('idle');
  const enabled = isPostHogEnabled();

  const trackEvent = useCallback((eventName: string, props: Record<string, unknown>) => {
    if (!isPostHogEnabled()) return;

    posthog.capture(eventName, props);
  }, []);
  const initAwareTrackEvent = useQueueUntilReady(trackEvent, isInitialized);

  const trackPageView = useCallback(({ path }: PageViewProps) => {
    if (!isPostHogEnabled()) return;

    posthog.capture('$pageview', { path });
  }, []);
  const initAwareTrackPageView = useQueueUntilReady(trackPageView, isInitialized);

  const identifyUser = useCallback(({ commonData: { id, fullName, ...commonData } }: UserData) => {
    if (!isPostHogEnabled()) return;

    posthog.identify(id, {
      ...omit(commonData, 'company'),
      name: fullName,
      // In PostHog session replay, it's not possible to filter by group properties or nested properties.
      // Therefore, we need to flatten the company data and add it as top-level properties.
      companyId: commonData.company.id,
      companyName: commonData.company.name,
      companyType: commonData.company.type,
      companyPlan: commonData.company.plan,
    });
    posthog.group('company', commonData.company.id, commonData.company);
  }, []);
  const initAwareIdentifyUser = useQueueUntilReady(identifyUser, isInitialized);

  const resetUserIdentity = useCallback(() => {
    if (!isPostHogEnabled()) return;

    posthog.reset();
  }, []);

  const initialize = useCallback(() => {
    if (!isPostHogEnabled() || !process.env.POSTHOG_API_KEY) return;

    if (initializationState.current !== 'idle') return;
    initializationState.current = 'loading';

    const apiKey = process.env.POSTHOG_API_KEY;

    return new Promise<void>((resolve) => {
      posthog.init(apiKey, {
        api_host: postHogUrl,
        person_profiles: 'always',
        session_recording: {
          maskCapturedNetworkRequestFn: (request) => {
            if (request.name.includes('/internal/auth/')) return null;

            if (request.name.includes('/internal/graphql')) {
              return redactRequest(request);
            }

            return request;
          },
        },
        loaded: () => {
          initializationState.current = 'done';
          setIsInitialized(true);
          resolve();
        },
      });
    });
  }, []);

  return useMemo(
    () => ({
      name: 'postHog',
      enabled,
      initialize,
      trackEvent: initAwareTrackEvent,
      trackPageView: initAwareTrackPageView,
      identifyUser: initAwareIdentifyUser,
      resetUserIdentity,
    }),
    [
      enabled,
      initialize,
      initAwareTrackEvent,
      initAwareTrackPageView,
      initAwareIdentifyUser,
      resetUserIdentity,
    ],
  );
};

const isPostHogEnabled = () =>
  areAnalyticsEnabledInEnv() &&
  Boolean(process.env.POSTHOG_API_KEY) &&
  process.env.REACT_APP_CYPRESS !== 'true';

export default usePostHogIntegration;
